import React, { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import styles from './AccountEditForm.module.scss'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import { countriesList } from '@src/utils/countryList'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { useFormNotify } from '@components/formNotify/useFormNotify.tsx'
import { AccountF } from '@src/logic/contexts/Accounts/AccountsFrontend.type'
import { Autocomplete, TextField } from '@mui/material'

export type AccountEditFormType = {
  id?: string
  accountName: string
  address1: string
  address2?: string
  address3?: string
  postCode: string
  city: string
  state: string
  country: { name: string; label?: string; code?: string } | string
}

export const defaultAccountEditValues = (): AccountEditFormType => ({
  accountName: '',
  address1: '',
  address2: '',
  address3: '',
  postCode: '',
  city: '',
  state: '',
  country: '',
})

const AccountEditValidation = yupResolver(
  yup
    .object({
      accountName: yup.string().trim().required('Account Name is required'),
      address1: yup.string().trim().required('Address is required'),
      address2: yup.string().trim().optional(),
      address3: yup.string().trim().optional(),
      postCode: yup.string().trim(),
      city: yup.string().trim().required('City is required'),
      state: yup.string().trim(),
      country: yup.mixed().test('country-shape', 'Country is required', (val: any) => {
        if (!val) return false
        if (typeof val === 'string') return val.trim().length > 0
        if (typeof val === 'object') return !!val.name && String(val.name).trim().length > 0
        return false
      }),
    })
    .required()
)

type Props = {
  onSubmit: (formData: AccountEditFormType) => void
  onCancel: () => void
  isSubmitting: boolean
  account?: AccountF | any | null // accepts the new shape with nested "address"
}

/** Case-insensitive match into countriesList; falls back to { name: string } */
const countryToOption = (value?: any) => {
  if (!value) return ''
  const str = typeof value === 'string' ? value : value?.name || value?.label || value?.code
  if (!str) return ''
  const lower = String(str).toLowerCase()
  return (
    countriesList.find((o: any) => String(o?.name || '').toLowerCase() === lower || String(o?.label || '').toLowerCase() === lower || String(o?.code || '').toLowerCase() === lower) || { name: str }
  )
}

const toFormValues = (account?: any | null): AccountEditFormType => {
  if (!account) return defaultAccountEditValues()

  // New prop shape
  const addr = account.address || {}
  const topCountry = account.accountCountry
  const resolvedCountry = addr.country ?? topCountry

  return {
    id: account.accountId || account.id,
    accountName: account.accountName || account.name || '',
    address1: addr.address_line_1 || account.address1 || account.address_1 || '',
    address2: addr.address_line_2 || account.address2 || account.address_2 || '',
    address3: addr.address_line_3 || account.address3 || account.address_3 || '',
    postCode: addr.postcode || account.postCode || account.post_code || '',
    city: addr.city || account.city || '',
    state: addr.state || account.state || '',
    country: countryToOption(resolvedCountry),
  }
}

/** Flat snake_case payload (kept as-is) */
export const parseAccountEditFormToBackend = (formData: AccountEditFormType) => ({
  account_id: formData.id,
  account_name: formData.accountName,
  address_line_1: formData.address1,
  address_line_2: formData.address2 || '',
  address_line_3: formData.address3 || '',
  postcode: formData.postCode,
  city: formData.city,
  state: formData.state,
  country: typeof formData.country === 'string' ? formData.country : formData.country?.name,
})

export const AccountEditForm: FC<Props> = ({ onSubmit, onCancel, isSubmitting = false, account }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, submitCount, isValid },
  } = useForm<AccountEditFormType>({
    defaultValues: toFormValues(account),
    resolver: AccountEditValidation,
    mode: 'onChange',
  })

  useFormNotify(errors, submitCount, isValid)

  useEffect(() => {
    reset(toFormValues(account))
  }, [account, reset])

  return (
    <form className={styles.accountForm} onSubmit={handleSubmit(onSubmit)}>
      {isSubmitting && (
        <div className={styles.LoadingOverlay}>
          <LxLoadingSpinner />
        </div>
      )}

      <Controller
        name='accountName'
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            fullWidth
            label='Account Name'
            value={value || ''}
            onChange={onChange}
            error={!!error}
            helperText={error?.message}
            variant='outlined'
            size='medium'
            margin='normal'
            required
            className={styles.muiInputField}
          />
        )}
      />

      <div className={styles.twoColumnGrid}>
        <div className={styles.leftColumn}>
          <Controller
            name='address1'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Address Line 1'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                required
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='address2'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Address Line 2'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='address3'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Address Line 3'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='postCode'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='ZIP / Postal Code'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />
        </div>
        <div className={styles.rightColumn}>
          <Controller
            name='city'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='City'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                required
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='state'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='State/Province/Region'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='country'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                fullWidth
                options={countriesList}
                getOptionLabel={(option: any) => option?.label || option?.name || ''}
                value={value || null}
                onChange={(_, newValue) => onChange(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label='Country' error={!!error} helperText={error?.message} variant='outlined' size='medium' margin='normal' required className={styles.muiInputField} />
                )}
                isOptionEqualToValue={(option: any, value: any) => {
                  if (!option || !value) return false
                  const optionName = option?.name || option?.label || ''
                  const valueName = typeof value === 'string' ? value : value?.name || value?.label || ''
                  return optionName === valueName
                }}
              />
            )}
          />
        </div>
      </div>

      <div className={styles.dialogActions}>
        <LxCancelButton onCancel={onCancel} classNames='lxActionButton lxActionButtonDefaultSize' />
        <LxSaveButton onSubmit={handleSubmit(onSubmit)} label='Save Changes' classNames='lxActionButton lxActionButtonFilled lxActionButtonDefaultSize' />
      </div>
    </form>
  )
}
